// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { ContractState, ContractViewModel, ServiceKind } from '@ail/contracts-api'
import { IContractViewModel } from '../services/Api/ContractApi'
import {
  allowedContractStatusesForAdminActive,
  allowedContractStatusesForAdminAll,
  defaultLanguage,
} from '../types/constants'
import { ContractStateEnum, DeltaGridColEnum } from '../types/enums'
import { ICustomerViewModel } from '../types/interfaces'

const locale = 'fr-CH'

const MS_PER_MINUTE = 60 * 1000
const MS_PER_HOUR = 60 * MS_PER_MINUTE
const MS_PER_DAY = 24 * MS_PER_HOUR

export function hasTimeElapsed(
  dateFrom: string | undefined,
  timeAmount: number,
  format: 'Days' | 'Hours' | 'Min' = 'Days',
  reverse?: boolean
) {
  const _date = new Date(dateFrom ?? 0)
  const interval = {
    Days: timeAmount * MS_PER_DAY,
    Hours: timeAmount * MS_PER_HOUR,
    Min: timeAmount * MS_PER_MINUTE,
  }[format]
  return reverse ? _date.getTime() - Date.now() >= interval : Date.now() - _date.getTime() >= interval
}

export function addDaysToDate(date: Date, nDays: number): Date {
  const maxDate = new Date(date)
  maxDate.setDate(maxDate.getDate() + nDays)
  return maxDate
}

/**
 * @returns A string with first letter upper case, with rest of the string lower case
 */
export function normalizeString(el: string) {
  return el.charAt(0).toUpperCase() + el.slice(1).toLowerCase()
}

export function isDateValid(date?: string | Date) {
  if (!date) return false
  const time = new Date(date).getTime()
  return time > 0 && time < 30000000000000
}

export const printDate = (date: Date | string | undefined, withTime = false) => {
  if (date) {
    if (!isDateValid(date)) {
      return '-'
    }
    const convertedDate = new Date(date)
    return withTime
      ? `${convertedDate.toLocaleDateString(locale)} - ${convertedDate.toLocaleTimeString(locale)}`
      : new Date(date).toLocaleDateString(locale)
  }
  return '-'
}

export function getDeltaIconPerKind(kind: string) {
  let icon
  switch (kind) {
    case DeltaGridColEnum.monthsDifForGrid:
      icon = 'icon-calendar'
      break
    case ServiceKind.Rmt || ServiceKind.Maintenance:
      icon = 'icon-rmt'
      break
    case DeltaGridColEnum.km:
      icon = 'icon-kilometers'
      break
    case DeltaGridColEnum.total:
      icon = 'icon-cost'
      break
    case ServiceKind.Tires:
      icon = 'icon-tires'
      break
    case ServiceKind.Fuel:
      icon = 'icon-fuel'
      break
    case ServiceKind.ReliefVehicle:
      icon = 'icon-reliefVehicle'
      break
    case ServiceKind.CarWash:
      icon = 'icon-car-wash'
      break
    case ServiceKind.Vignette:
      icon = 'icon-vignette'
      break
    case ServiceKind.RoadTax:
      icon = 'icon-road-tax'
      break
    case ServiceKind.Kasko:
      icon = 'icon-kasko'
      break
    case ServiceKind.Reestablishment:
      icon = 'icon-car-outfit'
      break
    case ServiceKind.Refurbishment:
      icon = 'icon-car-sponge'
      break
    default:
      icon = ''
  }

  return icon
}

export const defineContractState = (
  contract?: IContractViewModel,
  status?: ContractStateEnum,
  endDate?: Date,
  expiringDays: number = 90
) => {
  const future = new Date()
  const now = new Date()
  const contractEndDate = contract?.endDate || endDate
  const contractStateRaw = contract?.state || status
  future.setDate(future.getDate() + expiringDays)
  if (contractEndDate && now.getTime() <= contractEndDate.getTime() && contractEndDate.getTime() <= future.getTime()) {
    return ContractStateEnum.Expiring
  } else if (contract?.state === ContractStateEnum.Initialized && !!contract?.vinNumber) {
    return ContractStateEnum.SettingUp
  } else {
    return contractStateRaw
  }
}

export const zeroTimeZoneDate = (date: Date) => {
  const timeZoneDifference = date.getTimezoneOffset() * 60000
  const modifiedDate = new Date(date.getTime() - timeZoneDifference)
  return modifiedDate
}

interface DefineStateArgs {
  contract: ContractViewModel
  status?: ContractState
  endDate?: Date
  expDays?: number
}
export const defineContractStateNew = ({ contract, status, endDate, expDays }: DefineStateArgs) => {
  const future = new Date()
  const now = new Date()
  const contractEndDate = endDate || contract.endDate ? new Date(contract.endDate) : 0
  const contractStateRaw = status || contract.state
  future.setDate(future.getDate() + (expDays ? expDays : 0))
  if (contractEndDate && now.getTime() <= contractEndDate.getTime() && contractEndDate.getTime() <= future.getTime()) {
    return ContractStateEnum.Expiring
  } else if (contract?.state === ContractState.Initialized && !!contract?.vehicle?.motorNumber) {
    return ContractStateEnum.SettingUp
  } else {
    return contractStateRaw
  }
}

function reduceValidContractCount(contracts: number[], status: string): boolean {
  return contracts.length > 0
    ? contracts.reduce((ac, curr) => ac + curr) > 0
    : status === 'ALL'
    ? true
    : status === 'CANCELED'
    ? contracts?.length === 0
    : false
}

export const filterCustomersByGPstatus = (customers: ICustomerViewModel[], status: string) => {
  return customers.filter((c) => {
    const keys = Object.keys(c.contracts)
    return reduceValidContractCount(
      status === 'ACTIVE'
        ? allowedContractStatusesForAdminActive.map((s) => c.contracts[s]).filter((n) => n > 0)
        : status === 'ALL'
        ? allowedContractStatusesForAdminAll.map((s) => c.contracts[s]).filter((n) => n > 0)
        : keys.every((el) => !allowedContractStatusesForAdminActive.toString().includes(el)) &&
          keys.map((el) => c.contracts[el]).filter((n) => n >= 0),
      status
    )
  })
}
/**
 * Rounds a number to a specified number of decimal places.
 * @param val - Number to round.
 * @param toFixed - Number of decimal places to round to (default is 1).
 * @returns The rounded number.
 */
export const roundUpToFixed = (val: number, toFixed: number = 1) => {
  return Math.round(val * Math.pow(10, toFixed)) / Math.pow(10, toFixed)
}

/**
 *  Convert a number to localized format
 * @param num
 */
export const formatNumberToLocale = (num: number, minimumFractionDigits?: number, maximumFractionDigits?: number) => {
  return new Intl.NumberFormat(navigator.language, {
    maximumFractionDigits: maximumFractionDigits >= 0 ? maximumFractionDigits : 2,
    minimumFractionDigits: minimumFractionDigits >= 0 ? minimumFractionDigits : 2,
  }).format(num)
}

export const formatCurrencyToSwissLocale = (
  num: number,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number
) => {
  return new Intl.NumberFormat('de-CH', {
    minimumFractionDigits: minimumFractionDigits >= 0 ? minimumFractionDigits : 2,
    maximumFractionDigits: maximumFractionDigits >= 0 ? maximumFractionDigits : 2,
  }).format(num)
}
/**
 * Separates every 3 digits with a space
 * @param val Number to format, i.e 12345
 * @returns String, i.e '12 345'
 */
export function formatNumberWithSpaces(val: number) {
  return Math.round(val)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

export function isPlural(val: number): boolean {
  return typeof val === 'number' ? !val.toString().endsWith('1') : true
}

export function trDict(language: string, dict?: { [id: string]: string }) {
  return dict ? dict[language] || dict[defaultLanguage] || '' : ''
}

export function getRandomInt(from: number, to: number): number {
  return Math.floor(Math.random() * to) + from
}

export function convertContractIdToGUID(numericContractId: number): string {
  // numericContractId = numeric contract ID !NOT REFERENCE!

  const Miles = 0xee
  const MilesAdapter = 0xade0
  const EntityKind = 0x0c

  const buffer = new ArrayBuffer(16)
  const dataView = new DataView(buffer)

  dataView.setInt32(0, MilesAdapter)
  dataView.setInt16(4, Miles)
  dataView.setInt16(6, EntityKind)
  dataView.setInt32(8, numericContractId)
  dataView.setInt32(8, numericContractId, true)

  const uint8Array = new Uint8Array(buffer)

  const hex = Array.from(uint8Array, (byte) => byte.toString(16).padStart(2, '0'))

  const guidParts: string[] = [
    `${hex[0]}${hex[1]}${hex[2]}${hex[3]}`,
    `${hex[4]}${hex[5]}`,
    `${hex[6]}${hex[7]}`,
    `${hex[8]}${hex[9]}`,
    `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`,
  ]

  return guidParts.join('-')
}

export function isGuid(value: string): boolean {
  if (value.length !== 36) return false

  const dashPositions = [8, 13, 18, 23]
  for (const pos of dashPositions) {
    if (value[pos] !== '-') {
      return false
    }
  }

  const hexChars = /^[0-9a-fA-F-]+$/
  if (!hexChars.test(value)) {
    return false
  }

  return true
}
export function idToKEY(id: string): string {
  const KEY = id.toUpperCase().split('')
  KEY.unshift('$')
  return KEY.join('')
}
export function isKEY(value: string): boolean {
  if (value.length !== 37) return false
  if (value[0] !== '$') return false

  const dashPositions = [9, 14, 19, 24]
  for (const pos of dashPositions) {
    if (value[pos] !== '-') {
      return false
    }
  }
  return true
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(func: T, delay: number): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>

  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => func(...args), delay)
  }
}
